import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdClose,
  MdCloudUpload,
  MdCheckCircle,
  MdAdd,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdEdit,
} from "react-icons/md";
import Swal from "sweetalert2";
import affiliationService from "../../../../service/affiliation.service";
import auteurService from "../../../../service/auteur.service";

// TagInput component for affiliation selection
const TagInput = ({
  options,
  selectedValues = [],
  onChange,
  isLoading = false,
  placeholder = "Ajouter des affiliations...",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update suggestions when input changes
  useEffect(() => {
    if (inputValue.trim() === "") {
      setSuggestions([]);
      return;
    }

    const selectedIds = selectedValues.map((v) => v.toString());
    const filteredOptions = options
      .filter(
        (option) =>
          !selectedIds.includes(option.id_aff.toString()) &&
          option.name_aff.toLowerCase().includes(inputValue.toLowerCase())
      )
      .slice(0, 5); // Limit to 5 suggestions

    setSuggestions(filteredOptions);
  }, [inputValue, options, selectedValues]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Backspace" &&
      inputValue === "" &&
      selectedValues.length > 0
    ) {
      // Remove the last tag when backspace is pressed with empty input
      const newSelected = [...selectedValues];
      newSelected.pop();
      onChange(newSelected);
    } else if (e.key === "Enter" && suggestions.length > 0) {
      // Add the first suggestion when Enter is pressed
      e.preventDefault();
      addTag(suggestions[0].id_aff);
    }
  };

  const addTag = (optionId) => {
    const optionIdStr = optionId.toString();
    if (!selectedValues.includes(optionIdStr)) {
      const newSelected = [...selectedValues, optionIdStr];
      onChange(newSelected);
      setInputValue("");
      setSuggestions([]);

      // Focus the input again
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const removeTag = (optionId) => {
    onChange(selectedValues.filter((id) => id !== optionId.toString()));
  };

  // Get selected option objects
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.id_aff.toString())
  );

  return (
    <div className="w-full" ref={containerRef}>
      <div
        className={`flex flex-wrap rounded-lg border ${isFocused
          ? "border-[#57A863] ring-1 ring-[#57A863]"
          : "border-gray-300"
          } bg-white px-3 py-2 shadow-sm transition-all focus-within:border-[#57A863] focus-within:ring-1 focus-within:ring-[#57A863]`}
      >
        {/* Selected tags */}
        {selectedOptions.map((option) => (
          <div
            key={option.id_aff}
            className="m-0.5 flex items-center rounded-full bg-[#57A863]/10 px-2.5 py-1 text-sm text-[#57A863]"
          >
            {option.name_aff}
            <button
              type="button"
              onClick={() => removeTag(option.id_aff)}
              className="ml-1.5 rounded-full p-0.5 text-[#57A863] hover:bg-[#57A863]/20"
            >
              <MdClose className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        {/* Input field */}
        <div className="relative flex-grow">
          <input
            ref={inputRef}
            type="text"
            className="bg-transparent w-full min-w-[120px] border-none px-1.5 py-1 focus:outline-none focus:ring-0"
            placeholder={selectedOptions.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      {/* Suggestions dropdown */}
      {isFocused && (inputValue || suggestions.length > 0) && (
        <div className="absolute z-10 mt-1 w-full max-w-md rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {isLoading ? (
            <div className="flex h-20 items-center justify-center">
              <div className="border-t-transparent h-5 w-5 animate-spin rounded-full border-2 border-[#57A863]"></div>
              <span className="ml-2 text-sm text-gray-500">Chargement...</span>
            </div>
          ) : suggestions.length > 0 ? (
            <div className="max-h-[250px] overflow-y-auto py-1">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id_aff}
                  onClick={() => addTag(suggestion.id_aff)}
                  className="flex cursor-pointer items-center px-4 py-2 hover:bg-gray-100"
                >
                  <MdAdd className="mr-2 h-4 w-4 text-[#57A863]" />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-700">
                      {suggestion.name_aff}
                    </span>
                    {suggestion.country_aff && (
                      <span className="text-xs text-gray-500">
                        {suggestion.country_aff}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : inputValue ? (
            <div className="flex h-20 flex-col items-center justify-center p-4">
              <p className="text-sm text-gray-500">Aucun résultat trouvé</p>
              <p className="text-xs text-gray-400">
                Essayez un autre terme de recherche
              </p>
            </div>
          ) : (
            <div className="p-4">
              <p className="text-sm text-gray-500">
                Commencez à taper pour rechercher
              </p>
            </div>
          )}
        </div>
      )}

      {/* Selected count */}
      {selectedOptions.length > 0 && (
        <div className="mt-1 text-right">
          <span className="text-xs font-medium text-[#57A863]">
            {selectedOptions.length} affiliation(s) sélectionnée(s)
          </span>
        </div>
      )}
    </div>
  );
};

// Progress step indicator component
const ProgressSteps = ({ currentStep, totalSteps, onStepClick }) => {
  return (
    <div className="mb-6 flex items-center justify-center">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <React.Fragment key={index}>
          {/* Step circle */}
          <div
            onClick={() => onStepClick && onStepClick(index + 1)}
            className={`
              flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-sm font-medium
              ${index + 1 === currentStep
                ? "bg-[#57A863] text-white"
                : index + 1 < currentStep
                  ? "bg-[#57A863]/20 text-[#57A863]"
                  : "bg-gray-200 text-gray-500"
              }
            `}
          >
            {index + 1}
          </div>

          {/* Connector line (except after last step) */}
          {index < totalSteps - 1 && (
            <div
              className={`h-1 w-10 ${index + 1 < currentStep ? "bg-[#57A863]" : "bg-gray-200"
                }`}
            ></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Main AuthorCreationModal component
const AuthorCreationModal = ({
  isOpen,
  onClose,
  onCreateSuccess,
  onUpdateSuccess,
  author = null,
  isEditMode = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email_user: "",
    password: "",
    c_password: "",
    title_aut: "",
    orch_id: "",
    image_aut: null,
    id_aff: [],
    updatePassword: false, // Flag to indicate whether to update password or not
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [affiliations, setAffiliations] = useState([]);
  const [affiliationsLoading, setAffiliationsLoading] = useState(true);

  // Fetch affiliations
  useEffect(() => {
    const fetchAffiliations = async () => {
      setAffiliationsLoading(true);
      try {
        const result = await affiliationService.getAll(1, 1000);
        setAffiliations(result.data.data);
      } catch (error) {
        console.error("Erreur lors du chargement des affiliations:", error);
      } finally {
        setAffiliationsLoading(false);
      }
    };

    fetchAffiliations();
  }, []);

  // Reset form when modal opens and set values if in edit mode
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && author) {
        // Set author data for editing
        setFormData({
          first_name: author.first_name || "",
          last_name: author.last_name || "",
          email_user: author.email_user || "",
          password: "",
          c_password: "",
          title_aut: author.title_aut || "",
          image_aut: null,
          orch_id: author.orch_id || "",
          id_aff: author.affiliations
            ? author.affiliations.map((aff) => aff.id_aff.toString())
            : [],
          updatePassword: false,
        });

        // Set image preview if author has an image
        if (author.image_aut) {
          setImagePreview(`storage/${author.image_aut}`);
        } else {
          setImagePreview(null);
        }
      } else {
        // Reset for new author creation
        setFormData({
          first_name: "",
          last_name: "",
          email_user: "",
          password: "",
          c_password: "",
          title_aut: "",
          image_aut: null,
          orch_id: "",
          id_aff: [],
          updatePassword: false,
        });
        setImagePreview(null);
      }

      setValidationErrors({});
      setError(null);
      setCurrentStep(1);
    }
  }, [isOpen, isEditMode, author]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear validation error for the field
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: undefined });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image_aut: file });

      // Generate image preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Clear error if it exists
      if (validationErrors.image_aut) {
        setValidationErrors({ ...validationErrors, image_aut: undefined });
      }
    }
  };

  // Handle password update checkbox

  // Step-specific validation
  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      // Validate personal info
      if (!formData.first_name.trim()) {
        errors.first_name = "Le prénom est requis";
      }
      if (!formData.last_name.trim()) {
        errors.last_name = "Le nom est requis";
      }
      if (!formData.email_user.trim()) {
        errors.email_user = "L'email est requis";
      } else if (!/\S+@\S+\.\S+/.test(formData.email_user)) {
        errors.email_user = "Format d'email invalide";
      }
    } else if (step === 3) {
      // Validate password fields - only required in create mode or if update password is checked
      console.log("isEditMode")
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigation between steps
  const goToNextStep = () => {
    if (validateStep(currentStep) && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    // Only allow going to a step if all previous steps are valid
    let canNavigate = true;

    for (let i = currentStep; i > 0 && i < step; i--) {
      if (!validateStep(i)) {
        canNavigate = false;
        break;
      }
    }

    if (canNavigate) {
      setCurrentStep(step);
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate current step and entire form before submission
    if (validateStep(currentStep) && validateForm()) {
      // Loading indicator
      Swal.fire({
        title: isEditMode ? "Mise à jour en cours..." : "Création en cours...",
        html: `
        <div class="flex flex-col items-center">
          <div class="relative w-20 h-20">
            <div class="absolute top-0 left-0 w-20 h-20 border-4 border-[#57A863] border-opacity-20 rounded-full"></div>
            <div class="absolute top-0 left-0 w-20 h-20 border-4 border-transparent border-t-[#57A863] rounded-full animate-spin"></div>
          </div>
          <p class="mt-4 text-gray-700">Veuillez patienter pendant le traitement...</p>
        </div>
      `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      setIsLoading(true);
      setError(null);

      try {
        // Create FormData for submission
        const dataToSubmit = new FormData();

        // Add text fields
        dataToSubmit.append("first_name", formData.first_name.toString());
        dataToSubmit.append("last_name", formData.last_name.toString());
        dataToSubmit.append("email_user", formData.email_user.toString());
        dataToSubmit.append("orch_id", formData.orch_id.toString());

        // Add title if it exists
        if (formData.title_aut) {
          dataToSubmit.append("title_aut", formData.title_aut.toString());
        }

        // Add image if it exists (new upload)
        if (formData.image_aut instanceof File) {
          // Validate file type
          const fileType = formData.image_aut.type;
          const validTypes = [
            "image/jpeg",
            "image/png",
            "image/jpg",
            "image/gif",
            "image/svg+xml",
          ];

          if (!validTypes.includes(fileType)) {
            Swal.close();
            setValidationErrors({
              ...validationErrors,
              image_aut:
                "Le fichier doit être une image (jpeg, png, jpg, gif, svg).",
            });
            setIsLoading(false);
            return;
          }

          // Validate file size
          const maxSize = 10 * 1024 * 1024; // 10MB
          if (formData.image_aut.size > maxSize) {
            Swal.close();
            setValidationErrors({
              ...validationErrors,
              image_aut: "L'image ne doit pas dépasser 10MB.",
            });
            setIsLoading(false);
            return;
          }

          dataToSubmit.append("image_aut", formData.image_aut);
        } else if (isEditMode && !formData.image_aut && !imagePreview) {
          // If in edit mode and image was removed
          dataToSubmit.append("remove_image", "1");
        }

        if (formData.id_aff && formData.id_aff.length > 0) {
          const affiliationValue = formData.id_aff.join(",");
          dataToSubmit.append("id_aff", affiliationValue);
        }

        console.log("isEditMode:", isEditMode)

        let response;
        if (isEditMode) {
          // Update existing author
          response = await auteurService.updateAuteurs(author.id_aut, dataToSubmit);
        } else {
          // Create new author
          response = await auteurService.createAuteurs(dataToSubmit);
        }

        Swal.close();

        // Show success message
        await Swal.fire({
          icon: "success",
          title: isEditMode ? "Auteur modifié!" : "Auteur ajouté!",
          text: isEditMode
            ? "Les informations de l'auteur ont été mises à jour avec succès."
            : "Le nouvel auteur a été ajouté avec succès.",
          confirmButtonColor: "#57A863",
          showClass: {
            popup: "animate__animated animate__fadeInUp faster",
          },
          hideClass: {
            popup: "animate__animated animate__fadeOutDown faster",
          },
        });

        // Notify parent of success
        if (isEditMode && onUpdateSuccess) {
          onUpdateSuccess(response.data);
        } else if (!isEditMode && onCreateSuccess) {
          onCreateSuccess(response.data);
        }

        // Close modal
        onClose();
      } catch (err) {
        Swal.close();

        // Format error message
        let errorMessage = "Une erreur est survenue lors de l'opération.";

        if (err.message) {
          if (typeof err.message === "object") {
            // For Laravel validation errors
            const messages = [];
            Object.entries(err.message).forEach(([, fieldErrors]) => {
              if (Array.isArray(fieldErrors)) {
                messages.push(...fieldErrors);
              } else {
                messages.push(fieldErrors);
              }
            });
            errorMessage = messages.join("\n");
          } else {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);

        // Show error message
        await Swal.fire({
          icon: "error",
          title: "Erreur!",
          text: errorMessage,
          confirmButtonColor: "#d33",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Validate entire form
  const validateForm = () => {
    const errors = {};

    // Personal info validation
    if (!formData.first_name.trim()) {
      errors.first_name = "Le prénom est requis";
    }
    if (!formData.last_name.trim()) {
      errors.last_name = "Le nom est requis";
    }
    if (!formData.email_user.trim()) {
      errors.email_user = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email_user)) {
      errors.email_user = "Format d'email invalide";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setFormData({ ...formData, image_aut: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      if (validationErrors.image_aut) {
        setValidationErrors({ ...validationErrors, image_aut: undefined });
      }
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <h4 className="mb-4 text-lg font-medium text-gray-800">
              Informations personnelles
            </h4>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="first_name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Prénom <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border ${validationErrors.first_name
                    ? "border-red-500 ring-1 ring-red-500"
                    : "border-gray-300"
                    } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                  placeholder="Prénom"
                />
                {validationErrors.first_name && (
                  <p className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.first_name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="last_name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Nom <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border ${validationErrors.last_name
                    ? "border-red-500 ring-1 ring-red-500"
                    : "border-gray-300"
                    } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                  placeholder="Nom"
                />
                {validationErrors.last_name && (
                  <p className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.last_name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="title_aut"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Titre
                </label>
                <input
                  type="text"
                  id="title_aut"
                  name="title_aut"
                  value={formData.title_aut}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border ${validationErrors.title_aut
                    ? "border-red-500 ring-1 ring-red-500"
                    : "border-gray-300"
                    } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                  placeholder="Dr., Prof., etc."
                />
                {validationErrors.title_aut && (
                  <p className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.title_aut}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email_user"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Email <span className="ml-1 text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email_user"
                  name="email_user"
                  value={formData.email_user}
                  onChange={handleInputChange}
                  className={`w-full rounded-lg border ${validationErrors.email_user
                    ? "border-red-500 ring-1 ring-red-500"
                    : "border-gray-300"
                    } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                  placeholder="email@example.com"
                />
                {validationErrors.email_user && (
                  <p className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.email_user}
                  </p>
                )}
              </div>
            </div>
          </>
        );

      case 2:
        return (
          <>
            <h5 className="mb-4 text-lg font-medium text-gray-800">
              ORCID
            </h5>
            <div className="space-y-4">
              <input
                type="text"
                id="orch_id"
                name="orch_id"
                value={formData.orch_id}
                onChange={handleInputChange}
                className={`w-full rounded-lg border ${validationErrors.orch_id ? "border-red-500 ring-1 ring-red-500" : "border-gray-300"} px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                placeholder="0000-0001-2345-6789" />
            </div>

            <h4 className="mb-4 text-lg font-medium text-gray-800">
              Affiliations
            </h4>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="id_aff"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Sélectionnez vos affiliations
                </label>

                {affiliationsLoading ? (
                  <div className="flex h-10 items-center">
                    <div className="border-t-transparent mr-2 h-4 w-4 animate-spin rounded-full border-2 border-[#57A863]"></div>
                    <span className="text-sm text-gray-500">
                      Chargement des affiliations...
                    </span>
                  </div>
                ) : (
                  <TagInput
                    options={affiliations}
                    selectedValues={formData.id_aff}
                    onChange={(newSelected) => {
                      setFormData({
                        ...formData,
                        id_aff: newSelected,
                      });

                      // Clear error if it exists
                      if (validationErrors.id_aff) {
                        setValidationErrors({
                          ...validationErrors,
                          id_aff: undefined,
                        });
                      }
                    }}
                    isLoading={affiliationsLoading}
                    placeholder="Tapez pour rechercher des affiliations..."
                  />
                )}

                {validationErrors.id_aff && (
                  <p className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.id_aff}
                  </p>
                )}
              </div>
            </div>
          </>
        );

      case 3:
        return (
          <>
            <h4 className="mb-4 text-lg font-medium text-gray-800">
              Compte et photo
            </h4>
            <div className="grid grid-cols-1 gap-6">
              {/* Password fields
              <div className="space-y-4 hidden">
                {isEditMode && (
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.updatePassword}
                        onChange={handleUpdatePasswordToggle}
                        className="h-4 w-4 rounded border-gray-300 text-[#57A863] focus:ring-[#57A863]"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        Modifier le mot de passe
                      </span>
                    </label>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="password"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Mot de passe{" "}
                    {(!isEditMode || formData.updatePassword) && (
                      <span className="ml-1 text-red-500">*</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isEditMode && !formData.updatePassword}
                      className={`w-full rounded-lg border ${
                        validationErrors.password
                          ? "border-red-500 ring-1 ring-red-500"
                          : isEditMode && !formData.updatePassword
                          ? "border-gray-200 bg-gray-100"
                          : "border-gray-300"
                      } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                      placeholder={
                        isEditMode && !formData.updatePassword
                          ? "••••••••"
                          : "Minimum 8 caractères"
                      }
                    />
                    {(!isEditMode || formData.updatePassword) && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Masquer" : "Afficher"}
                      </button>
                    )}
                  </div>
                  {validationErrors.password && (
                    <p className="mt-1 flex items-center text-sm text-red-600">
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {validationErrors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="c_password"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Confirmer le mot de passe{" "}
                    {(!isEditMode || formData.updatePassword) && (
                      <span className="ml-1 text-red-500">*</span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="c_password"
                      name="c_password"
                      value={formData.c_password}
                      onChange={handleInputChange}
                      disabled={isEditMode && !formData.updatePassword}
                      className={`w-full rounded-lg border ${
                        validationErrors.c_password
                          ? "border-red-500 ring-1 ring-red-500"
                          : isEditMode && !formData.updatePassword
                          ? "border-gray-200 bg-gray-100"
                          : "border-gray-300"
                      } px-4 py-3 shadow-sm transition-all focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]`}
                      placeholder={
                        isEditMode && !formData.updatePassword
                          ? "••••••••"
                          : "Confirmez le mot de passe"
                      }
                    />
                  </div>
                  {validationErrors.c_password && (
                    <p className="mt-1 flex items-center text-sm text-red-600">
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {validationErrors.c_password}
                    </p>
                  )}
                </div>
              </div>
              */}
              {/* Image upload */}
              <div className="flex flex-col items-center space-y-4">
                {/* Image preview */}
                {imagePreview ? (
                  <div className="group relative h-40 w-40 overflow-hidden rounded-full border-4 border-gray-200 shadow-md transition-all duration-300 hover:border-[#57A863]">
                    <img
                      src={imagePreview}
                      alt="Aperçu"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="bg-black/50 absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData((prev) => ({
                            ...prev,
                            image_aut: null,
                          }));
                        }}
                        className="rounded-full bg-red-500 p-2 text-white hover:bg-red-600"
                      >
                        <MdClose className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-40 w-40 items-center justify-center rounded-full border-4 border-gray-200 bg-gray-50">
                    <span className="text-sm text-gray-500">Aucune image</span>
                  </div>
                )}

                {/* Drag & drop zone */}
                <div
                  className={`relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed 
                    ${isDragging
                      ? "border-[#57A863] bg-green-50"
                      : validationErrors.image_aut
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300 bg-gray-50"
                    }
                    p-4 transition-all duration-300 hover:border-[#57A863] hover:bg-green-50`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="image_aut"
                    name="image_aut"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  />
                  <MdCloudUpload
                    className={`mb-2 h-8 w-8 ${isDragging
                      ? "text-[#57A863]"
                      : validationErrors.image_aut
                        ? "text-red-400"
                        : "text-gray-400"
                      }`}
                  />
                  <p
                    className={`text-center text-sm ${isDragging
                      ? "text-[#57A863]"
                      : validationErrors.image_aut
                        ? "text-red-500"
                        : "text-gray-500"
                      }`}
                  >
                    {formData.image_aut instanceof File
                      ? formData.image_aut.name
                      : isDragging
                        ? "Déposez l'image ici"
                        : isEditMode && author?.image_aut && !imagePreview
                          ? "Modifier l'image"
                          : "Cliquez ou glissez une image ici"}
                  </p>
                  <p className="mt-1 text-center text-xs text-gray-400">
                    PNG, JPG, GIF jusqu'à 10MB
                  </p>
                </div>

                {validationErrors.image_aut && (
                  <p className="mt-1 flex items-center text-sm text-red-600">
                    <svg
                      className="mr-1 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.image_aut}
                  </p>
                )}
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            className="relative w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-2xl"
          >
            {/* Header with gradient */}
            <div className="relative bg-gradient-to-r from-[#57A863] to-[#3d9448] px-6 py-5 text-white">
              <h3 className="text-xl font-bold">
                {isEditMode ? (
                  <span className="flex items-center">
                    <MdEdit className="mr-2 h-5 w-5" />
                    Modifier un auteur
                  </span>
                ) : (
                  <span className="flex items-center">
                    <MdAdd className="mr-2 h-5 w-5" />
                    Ajouter un nouvel auteur
                  </span>
                )}
              </h3>
              <button
                onClick={onClose}
                className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-1.5 text-white transition-all hover:rotate-90 hover:bg-white/30"
                aria-label="Fermer"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>

            {/* Progress steps */}
            <div className="bg-gray-50 px-6 py-3">
              <ProgressSteps
                currentStep={currentStep}
                totalSteps={totalSteps}
                onStepClick={goToStep}
              />
            </div>

            {/* Form content */}
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto p-6">
              <form className="space-y-6">
                {renderStepContent()}

                {/* Error message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-lg bg-red-50 p-4 text-sm text-red-700"
                  >
                    <div className="flex">
                      <svg
                        className="mr-2 h-5 w-5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <h3 className="font-medium">Une erreur est survenue</h3>
                        <div className="mt-1">{error}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </form>
            </div>

            {/* Footer with navigation */}
            <div className="border-t bg-gray-50 px-6 py-4">
              <div className="flex justify-between">
                {/* Back button (hidden on first step) */}
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={goToPrevStep}
                    className="flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50"
                  >
                    <MdKeyboardArrowLeft className="mr-1 h-5 w-5" />
                    Précédent
                  </button>
                ) : (
                  <div></div> // Placeholder for spacing
                )}

                {/* Next/Submit button */}
                <button
                  type="button"
                  onClick={
                    currentStep === totalSteps ? handleSubmit : goToNextStep
                  }
                  className="flex items-center rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-5 py-2 font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 disabled:opacity-70"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="mr-2 h-4 w-4 animate-spin"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Traitement...
                    </span>
                  ) : currentStep === totalSteps ? (
                    <span className="flex items-center">
                      <MdCheckCircle className="mr-1.5 h-4 w-4" />
                      {isEditMode ? "Mettre à jour" : "Ajouter"}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Suivant
                      <MdKeyboardArrowRight className="ml-1 h-5 w-5" />
                    </span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AuthorCreationModal;
